import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Steps,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PaperClipOutlined,
  PayCircleOutlined,
  SendOutlined,
  StopOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { financeService, mediaUrl } from "../../services/financeService";
import { formatLkr } from "../../utils/currency";
import { getStoredUser, hasPermission } from "../../utils/permissions";
import type { BankAccount, FinanceVoucherDetail } from "../../types";
import VoucherFormModal from "./VoucherFormModal";
import { StatusTag } from "./VouchersPage";

const { Title, Text, Paragraph } = Typography;

const ATTACHMENT_KINDS = [
  { value: "invoice", label: "Invoice" },
  { value: "receipt", label: "Receipt" },
  { value: "payment_proof", label: "Payment proof" },
  { value: "quotation", label: "Quotation" },
  { value: "other", label: "Other" },
];

const VoucherDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<FinanceVoucherDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [banks, setBanks] = useState<BankAccount[]>([]);

  // Modal-driven actions that need a reason or a target account.
  const [rejectOpen, setRejectOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [payBankId, setPayBankId] = useState<string | undefined>();
  const [receiveBankId, setReceiveBankId] = useState<string | undefined>();
  const [attachmentKind, setAttachmentKind] = useState("invoice");

  const user = getStoredUser();
  const currentUser = user?.id || null;

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      setDetail(await financeService.getVoucher(id));
      setError(null);
    } catch (e: any) {
      setError(e?.message || "Failed to load the voucher");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    financeService
      .getBankAccounts()
      .then(setBanks)
      .catch(() => setBanks([]));
  }, []);

  const run = async (label: string, action: () => Promise<any>, after?: () => void) => {
    setBusy(true);
    try {
      await action();
      message.success(label);
      if (after) after();
      await load();
    } catch (e: any) {
      message.error(e?.message || `${label} failed`);
    } finally {
      setBusy(false);
    }
  };

  const d = detail;
  const type = d?.voucher_type || "expense";
  const canRecord = hasPermission(type === "income" ? "finance:income" : "finance:expense");
  const canApprove = hasPermission("finance:approve");
  const isCreator = !!d && !!currentUser && d.created_by === currentUser;
  const isDraft = !!d && (d.status === "draft" || d.status === "rejected");
  const canWorkflow = !!d && canApprove && d.status === "pending_approval" && !isCreator;

  const lineTotals = useMemo(() => {
    if (!d) return { debit: 0, credit: 0 };
    return (d.journal_lines || []).reduce(
      (acc, l) => {
        if (l.side === "debit") acc.debit += Number(l.amount);
        else acc.credit += Number(l.amount);
        return acc;
      },
      { debit: 0, credit: 0 },
    );
  }, [d]);

  if (loading && !d) {
    return <div style={{ padding: 24 }}>Loading…</div>;
  }

  if (!d) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          showIcon
          title={/not found/i.test(error || "") ? "Voucher not found" : "Failed to load voucher"}
          description={error || ""}
        />
        <Button style={{ marginTop: 16 }} icon={<ArrowLeftOutlined />} onClick={() => navigate("/finance/vouchers")}>
          Back to vouchers
        </Button>
      </div>
    );
  }

  const workflowStep =
    d.status === "draft"
      ? 0
      : d.status === "rejected"
        ? 1
        : d.status === "pending_approval"
          ? 1
          : d.status === "void"
            ? 3
            : 3;

  const creditAccounts = d.journal_lines.filter((l) => l.side === "credit");
  const usesCredit = creditAccounts.some((l) => l.account_code === "2010");

  // An income recognised on credit debited 1100, and until somebody collects
  // it nothing in the system can take it back out. Cash and card income never
  // reached 1100, and an expense has no receivable to clear — so for those
  // the action does not merely lack permission, it does not exist.
  const canReceive =
    d.status === "posted" &&
    d.voucher_type === "income" &&
    d.payment_method === "credit" &&
    hasPermission("finance:income");

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }} wrap>
        <Space wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/finance/vouchers")}>
            Back
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {d.voucher_no}
          </Title>
          <StatusTag status={d.status} />
          <Text type="secondary">{dayjs(d.business_date).format("MMM D, YYYY")}</Text>
        </Space>
        <Space wrap>
          {isDraft && canRecord && (
            <Button icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
              Edit
            </Button>
          )}
          {isDraft && canRecord && (
            <Button
              icon={<SendOutlined />}
              type="primary"
              loading={busy}
              onClick={() => run("Submitted for approval", () => financeService.submitVoucher(d.id))}
            >
              Submit
            </Button>
          )}
          {d.status === "pending_approval" && canWorkflow && (
            <>
              <Button
                icon={<CheckOutlined />}
                type="primary"
                loading={busy}
                onClick={() => run("Approved", () => financeService.approveVoucher(d.id))}
              >
                Approve
              </Button>
              <Button icon={<CloseOutlined />} danger onClick={() => setRejectOpen(true)}>
                Reject
              </Button>
            </>
          )}
          {d.status === "approved" && type === "expense" && usesCredit && hasPermission("finance:expense") && (
            <Button icon={<PayCircleOutlined />} type="primary" onClick={() => setPayOpen(true)}>
              Record payment
            </Button>
          )}
          {canReceive && (
            <Button icon={<PayCircleOutlined />} type="primary" onClick={() => setReceiveOpen(true)}>
              Record receipt
            </Button>
          )}
          {["posted", "approved", "paid"].includes(d.status) && canRecord && (
            <Button icon={<StopOutlined />} danger onClick={() => setVoidOpen(true)}>
              Void
            </Button>
          )}
          {isDraft && canRecord && (
            <Button
              icon={<DeleteOutlined />}
              danger
              loading={busy}
              onClick={() => run("Draft deleted", () => financeService.deleteVoucher(d.id), () => navigate("/finance/vouchers"))}
            >
              Delete
            </Button>
          )}
        </Space>
      </Space>

      {error && <Alert type="error" showIcon title="Error" description={error} style={{ marginBottom: 16 }} />}

      {d.status === "rejected" && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          title="Rejected"
          description={
            d.approvals.filter((a) => a.decision === "rejected").slice(-1)[0]?.notes ||
            "Returned to draft — edit and resubmit."
          }
        />
      )}

      {d.status === "pending_approval" && isCreator && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          title="Awaiting approval"
          description={`You created this voucher, so you cannot approve it yourself. ${d.approvals_required - d.approvals_count} more distinct approver(s) needed.`}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card title="Voucher" size="small" style={{ marginBottom: 16 }}>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="Type">{d.voucher_type === "income" ? "Income" : "Expense"}</Descriptions.Item>
              <Descriptions.Item label="Amount">
                <Text strong>{formatLkr(d.amount + d.tax)}</Text>
                {d.tax > 0 && <Text type="secondary"> (incl. {formatLkr(d.tax)} tax)</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Department">{d.department_name}</Descriptions.Item>
              <Descriptions.Item label="Account">
                <Text code>{d.account_code}</Text> {d.account_name}
              </Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>
                {d.description}
              </Descriptions.Item>
              <Descriptions.Item label="Payment">{d.payment_method.replace("_", " ")}</Descriptions.Item>
              <Descriptions.Item label="Bank / cash account">{d.bank_account_name || "—"}</Descriptions.Item>
              <Descriptions.Item label="Party">{d.party || "—"}</Descriptions.Item>
              <Descriptions.Item label="Reference">{d.reference_no || "—"}</Descriptions.Item>
              <Descriptions.Item label="Created by">
                {d.created_by_name || "—"} · {dayjs(d.created_at).format("MMM D, YYYY HH:mm")}
              </Descriptions.Item>
              <Descriptions.Item label="Journal">
                {d.journal_id ? (
                  <Tag color="green">posted</Tag>
                ) : (
                  <Tag>not posted</Tag>
                )}
              </Descriptions.Item>
              {d.remarks && (
                <Descriptions.Item label="Remarks" span={2}>
                  <Paragraph style={{ margin: 0 }}>{d.remarks}</Paragraph>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          <Card
            title="Journal lines"
            size="small"
            style={{ marginBottom: 16 }}
            extra={
              d.journal_lines.length > 0 ? (
                <Text type={lineTotals.debit === lineTotals.credit ? "success" : "danger"}>
                  {formatLkr(lineTotals.debit)} = {formatLkr(lineTotals.credit)}
                </Text>
              ) : null
            }
          >
            {d.journal_lines.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Nothing posted to the ledger yet"
              />
            ) : (
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ x: 640 }}
                dataSource={d.journal_lines}
                columns={[
                  { title: "Account", key: "account", render: (_: any, l) => (<><Text code>{l.account_code}</Text> {l.account_name}</>) },
                  { title: "Department", dataIndex: "department_name", width: 150, ellipsis: true },
                  {
                    title: "Side",
                    dataIndex: "side",
                    width: 80,
                    render: (s: string) => <Tag color={s === "debit" ? "blue" : "orange"}>{s}</Tag>,
                  },
                  {
                    title: "Amount",
                    dataIndex: "amount",
                    width: 140,
                    align: "right" as const,
                    render: (v: number) => formatLkr(v),
                  },
                  { title: "Memo", dataIndex: "memo", ellipsis: true, render: (v: string) => v || "—" },
                ]}
              />
            )}
          </Card>

          <Card
            title="Attachments"
            size="small"
            extra={
              canRecord && d.status !== "void" ? (
                <Space>
                  <Select
                    size="small"
                    style={{ width: 150 }}
                    value={attachmentKind}
                    onChange={setAttachmentKind}
                    options={ATTACHMENT_KINDS}
                  />
                  <Upload
                    showUploadList={false}
                    beforeUpload={(file) => {
                      run("Attachment uploaded", () =>
                        financeService.uploadAttachment(d.id, file, attachmentKind),
                      );
                      return false;
                    }}
                  >
                    <Button size="small" icon={<UploadOutlined />}>
                      Upload
                    </Button>
                  </Upload>
                </Space>
              ) : null
            }
          >
            {d.attachments.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No files attached" />
            ) : (
              <Space direction="vertical" style={{ width: "100%" }}>
                {d.attachments.map((a) => (
                  <Space key={a.id} wrap>
                    <PaperClipOutlined />
                    <a href={mediaUrl(a.url)} target="_blank" rel="noreferrer">
                      {a.original_name}
                    </a>
                    <Tag>{ATTACHMENT_KINDS.find((k) => k.value === a.kind)?.label || a.kind}</Tag>
                    {canRecord && (
                      <Button
                        size="small"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => run("Attachment removed", () => financeService.deleteAttachment(a.id))}
                      />
                    )}
                  </Space>
                ))}
              </Space>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card title="Workflow" size="small" style={{ marginBottom: 16 }}>
            <Steps
              direction="vertical"
              size="small"
              current={workflowStep}
              items={[
                { title: "Drafted", description: d.created_by_name || "—" },
                {
                  title: "Submitted",
                  description:
                    d.status === "draft" ? "Not submitted" : `${d.approvals_count}/${d.approvals_required} approvals`,
                },
                {
                  title: "Approved",
                  description: d.approved_at ? dayjs(d.approved_at).format("MMM D, HH:mm") : "Pending",
                },
                {
                  title: d.voucher_type === "income" ? "Posted" : "Posted & settled",
                  description: d.journal_id ? "In the ledger" : "Not posted",
                },
              ]}
            />
          </Card>

          <Card title="Approval trail" size="small">
            {d.approvals.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No decisions yet" />
            ) : (
              <Space direction="vertical" style={{ width: "100%" }}>
                {d.approvals.map((a) => (
                  <div key={a.id}>
                    <Space wrap>
                      <Tag color={a.decision === "approved" ? "green" : a.decision === "rejected" ? "red" : "blue"}>
                        {a.decision}
                      </Tag>
                      <Text strong>{a.approver_name}</Text>
                      <Text type="secondary">{a.approver_role}</Text>
                      <Text type="secondary">{dayjs(a.created_at).format("MMM D, HH:mm")}</Text>
                    </Space>
                    {a.notes && <div style={{ fontSize: 12, color: "#8c8c8c" }}>{a.notes}</div>}
                  </div>
                ))}
              </Space>
            )}
          </Card>
        </Col>
      </Row>

      <VoucherFormModal
        open={editOpen}
        initialType={type}
        editing={d}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          message.success("Voucher updated");
          load();
        }}
      />

      <Modal
        open={rejectOpen}
        title="Reject voucher"
        okText="Reject"
        okButtonProps={{ danger: true, disabled: !reason.trim() }}
        confirmLoading={busy}
        onCancel={() => setRejectOpen(false)}
        onOk={() =>
          run("Voucher rejected", () => financeService.rejectVoucher(d.id, reason.trim()), () => {
            setRejectOpen(false);
            setReason("");
          })
        }
      >
        <Input.TextArea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why is this being rejected? (required)"
        />
      </Modal>

      <Modal
        open={voidOpen}
        title="Void voucher"
        okText="Void and post reversal"
        okButtonProps={{ danger: true, disabled: !reason.trim() }}
        confirmLoading={busy}
        onCancel={() => setVoidOpen(false)}
        onOk={() =>
          run("Voucher voided — reversal posted", () => financeService.voidVoucher(d.id, reason.trim()), () => {
            setVoidOpen(false);
            setReason("");
          })
        }
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          title="The ledger is append-only"
          description="Voiding posts a reversing journal; the original entries stay untouched."
        />
        <Input.TextArea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for voiding (required)"
        />
      </Modal>

      <Modal
        open={payOpen}
        title="Record payment"
        okText="Post payment"
        confirmLoading={busy}
        onCancel={() => setPayOpen(false)}
        onOk={() =>
          payBankId &&
          run("Payment recorded", () => financeService.payVoucher(d.id, payBankId), () => setPayOpen(false))
        }
      >
        <Select
          style={{ width: "100%" }}
          placeholder="Pay from"
          value={payBankId}
          onChange={setPayBankId}
          options={banks
            .filter((b) => b.is_active && b.type !== "cash")
            .map((b) => ({ value: b.id, label: `${b.name} (${b.account_code})` }))}
        />
      </Modal>

      <Modal
        open={receiveOpen}
        title="Record receipt"
        okText="Post receipt"
        confirmLoading={busy}
        onCancel={() => setReceiveOpen(false)}
        onOk={() =>
          receiveBankId &&
          run("Receipt recorded", () => financeService.receiveVoucher(d.id, receiveBankId), () =>
            setReceiveOpen(false),
          )
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          title="Clears the receivable"
          description={`Posts Dr tender · Cr 1100 for ${formatLkr(d.amount + d.tax)} and moves the voucher to paid. One full receipt per voucher — this schema has no partial settlements.`}
        />
        <Select
          style={{ width: "100%" }}
          placeholder="Received into"
          value={receiveBankId}
          onChange={setReceiveBankId}
          options={banks
            .filter((b) => b.is_active && b.type !== "card")
            .map((b) => ({ value: b.id, label: `${b.name} (${b.account_code})` }))}
        />
      </Modal>
    </div>
  );
};

export default VoucherDetailPage;
